/**
 * Small presentational pieces for the Outreach tab, in the look of Theresa's "Sourcing & Outreach"
 * draft (FINN tokens, 2px radius, evidence chips, a funnel strip). No state, no data access.
 */
import type { ReactNode } from 'react';
import { cn } from '../../../../shared';
import type { GapCriterion, OutreachCandidate } from '../../types/taOutreach';
import { ownerFirstName } from './ownerName';

type Tone = 'grey' | 'blue' | 'amber' | 'red' | 'green';
const TONE: Record<Tone, string> = {
  grey: 'bg-[#E9EAEC] text-[#707070]',
  blue: 'bg-[#DBEBFC] text-[#0072EA]',
  amber: 'bg-[#FCEEE2] text-[#B96716]',
  red: 'bg-[#FCDFE4] text-[#E71D3F]',
  green: 'bg-[#DEF5E6] text-[#1D9247]',
};

/**
 * Whose person this is, next to their name (Sofia, Oct 27 2026): the role's pipeline is the team's,
 * so every card and Log row names the recruiter who found, added or sent them.
 */
export function OwnerName({ owner }: { owner?: string | null }) {
  const name = ownerFirstName(owner);
  return name ? (
    <span className="text-xs font-normal text-[#707070]" title={`On ${name}'s list`}>
      {name}
    </span>
  ) : null;
}

/**
 * Write & send cards: who SIGNS the InMail, not who added the person (Sofia, Oct 28 2026: every card
 * said "Luisa" after Sofia pressed Update, so it looked as if nothing changed). The sender's first name
 * from the draft; without a draft, the owner as before. The tooltip says both when they differ.
 */
export function SenderName({ draft, owner }: { draft?: { sentAs?: { senderName?: string | null } | null } | null; owner?: string | null }) {
  const added = ownerFirstName(owner);
  const sender = draft?.sentAs?.senderName?.trim() || null;
  if (!sender) return <OwnerName owner={owner} />;
  return (
    <span
      className="text-xs font-normal text-[#707070]"
      title={added && added !== sender ? `Signed by ${sender} · added by ${added}` : `Signed by ${sender}`}
      data-testid="sender-name"
    >
      {sender}
    </span>
  );
}

export function Badge({ tone = 'grey', children }: { tone?: Tone; children: ReactNode }) {
  return <span className={cn('inline-block rounded px-[7px] py-0.5 text-xs', TONE[tone])}>{children}</span>;
}

/** What the profile shows for one criterion: green = shown, amber = not known, red = says otherwise. */
export function EvidenceChip({ c }: { c: GapCriterion }) {
  const tone = c.verdict === 'evidenced' ? 'green' : c.verdict === 'contradicted' ? 'red' : 'amber';
  const prefix = c.verdict === 'evidenced' ? '' : c.verdict === 'contradicted' ? 'Profile says otherwise: ' : 'Not known: ';
  return (
    <span className={cn('inline-block rounded px-2 py-1 text-xs', TONE[tone])} title={c.evidence ? `“${c.evidence}”` : undefined}>
      {prefix}
      {c.label}
    </span>
  );
}

export function Avatar({ name }: { name: string }) {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('');
  return (
    <span className="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-full bg-[#E9EAEC] text-[13px] font-semibold text-[#979797]">
      {initials}
    </span>
  );
}

/** The funnel strip under a campaign title. `null` renders as a dash: no data source, not zero. */
export function FunnelStrip({ items }: { items: { label: string; value: number | null }[] }) {
  return (
    <div className="mb-[18px] flex flex-wrap gap-6 rounded-md border border-solid border-[#E9EAEC] bg-[#F8F8F8] px-[18px] py-[13px]">
      {items.map((i) => (
        <span key={i.label} className="text-xs text-[#707070]">
          <b className="block text-[19px] font-semibold leading-[26px] text-[#1A1A1A] [font-variant-numeric:tabular-nums]">{i.value ?? '—'}</b>
          {i.label}
        </span>
      ))}
    </div>
  );
}

/** Second-level tabs (Brief · Candidates · Outreach queue · Replies), underline style. */
export function SubTabs<T extends string>({
  tabs,
  value,
  onChange,
}: {
  tabs: { id: T; label: string; count?: number | null }[];
  value: T;
  onChange: (t: T) => void;
}) {
  return (
    <div className="mb-[22px] flex flex-wrap gap-0.5 border-b border-solid border-[#E9EAEC]" role="tablist">
      {tabs.map((t) => (
        <button
          key={t.id}
          type="button"
          role="tab"
          aria-selected={value === t.id}
          onClick={() => onChange(t.id)}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-t-sm px-3.5 py-2.5 text-sm font-semibold',
            value === t.id ? 'text-[#1A1A1A] shadow-[inset_0_-2px_0_#1A1A1A]' : 'text-[#707070] hover:bg-[#F8F8F8] hover:text-[#1A1A1A]'
          )}
        >
          {t.label}
          {t.count !== undefined && t.count !== null && <span className="text-xs font-normal text-[#979797]">{t.count}</span>}
        </button>
      ))}
    </div>
  );
}

export function Card({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('rounded-md border border-solid border-[#E9EAEC] bg-white px-[22px] py-[18px]', className)}>{children}</div>;
}

export function AgentLabel({ children }: { children: ReactNode }) {
  return <span className="mb-2.5 block text-[11px] font-semibold uppercase tracking-[.05em] text-[#979797]">{children}</span>;
}



export type Step<T extends string> = { id: T; label: string; hint: string; done: boolean; count?: number | null };

/**
 * The guided steps of one role (Sofia, Oct 19 2026): where you are, what is done, what comes next.
 * Every step stays clickable — the order is guidance, not a lock.
 */
export function Stepper<T extends string>({ steps, value, onChange }: { steps: Step<T>[]; value: T; onChange: (t: T) => void }) {
  return (
    <ol className="m-0 mb-[22px] grid list-none gap-2 p-0 sm:grid-cols-4" aria-label="Steps">
      {steps.map((s, i) => {
        const current = s.id === value;
        return (
          <li key={s.id}>
            <button
              type="button"
              aria-current={current ? 'step' : undefined}
              onClick={() => onChange(s.id)}
              className={cn(
                'flex h-full w-full items-start gap-2.5 rounded-md border border-solid px-3 py-2.5 text-left',
                current ? 'border-[#1A1A1A] bg-white' : 'border-[#E9EAEC] bg-[#F8F8F8] hover:border-[#979797]'
              )}
            >
              <span
                className={cn(
                  'grid h-6 w-6 shrink-0 place-items-center rounded-full text-xs font-semibold',
                  s.done ? 'bg-[#1D9247] text-white' : current ? 'bg-[#1A1A1A] text-white' : 'bg-[#E9EAEC] text-[#707070]'
                )}
                aria-hidden="true"
              >
                {s.done ? '✓' : i + 1}
              </span>
              <span className="min-w-0">
                <span className={cn('block text-sm font-semibold', current ? 'text-[#1A1A1A]' : 'text-[#707070]')}>
                  <span className="sr-only">{`Step ${i + 1}${s.done ? ', done' : ''}: `}</span>
                  {s.label}
                  {s.count ? <span className="ml-1.5 text-xs font-normal text-[#979797]">{s.count}</span> : null}
                </span>
                <span className="block text-xs text-[#979797]">{s.hint}</span>
              </span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}

/** What to do in this step, at its top. */
export function StepIntro({ n, total, children }: { n: number; total: number; children: ReactNode }) {
  return (
    <p className="mb-4 rounded-md border border-solid border-[#DBEBFC] bg-[#F3F8FE] px-4 py-3 text-[13px] text-[#1A1A1A]">
      <b>
        Step {n} of {total}.
      </b>{' '}
      {children}
    </p>
  );
}

/**
 * Drafts approve themselves (Sofia, Nov 2 2026): what Write & send shows of it. The summary line
 * counts the approved ones (how many by themselves) and the ones to review; a card says
 * "auto-approved", or in one short line why the auto-draft held it.
 */
export function QueueSummary({ drafts, ready }: { drafts: OutreachCandidate[]; ready: OutreachCandidate[] }) {
  const auto = ready.filter((c) => c.autoApproved).length;
  return (
    <span>
      <b>{ready.length}</b> approved{auto ? ` (${auto} automatically)` : ''} · <b>{drafts.length}</b> to review. Everyone you accepted on the shortlist is here.
    </span>
  );
}

/** Under the card's name: "auto-approved", or why it waits for review. Nothing otherwise. */
export function AutoApproveNote({ c }: { c: OutreachCandidate }) {
  if (c.status === 'ready' && c.autoApproved) return <span className="block text-xs text-[#1D9247]">auto-approved</span>;
  if (c.status === 'drafted' && c.autoHeld) return <span className="block max-w-[560px] truncate text-xs text-[#B3261E]" title={c.autoHeld}>Held for review: {c.autoHeld}</span>;
  return null;
}
